import { Component, EventEmitter, OnDestroy, OnInit, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { AppService } from '../../app.service';
import { UserAdminService } from '../user-admin.service';
import { GtwSnackbarService } from '../../shared/_services/gtw-snackbar.service';
import { GlobalService } from '../../global/services/global.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import { resolveRowPayload } from '../../shared/workspace-element-context';
import { AdminScreenContextService } from '../../admin/shared/admin-screen-context.service';
import * as _ from 'lodash-es';

@Component({
    selector: 'gtw-user-details',
    templateUrl: './user-details.component.html',
    styleUrls: ['./user-details.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class UserDetailsComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  rowData: any = {};
  @Output() emitEvent = new EventEmitter<string>();

  private readonly subs = new Subscription();

  user: any = {};
  loading = false;
  crudLoading = false;
  clientList: any[] = [];
  defaultClientData: any[] = [];

  active = false;
  client_active: any;
  user_id = '';

  avatarInitials = '';

  user_types = [
    { label: 'User', value: 1 },
    { label: 'Editor', value: 2 },
    { label: 'Admin', value: 3 },
    { label: 'Developer', value: 4 },
    { label: 'Super Admin', value: 5 },
  ];

  selectedUserType: any = null;

  role_types = [
    { label: 'IT', value: 'IT' },
    { label: 'External', value: 'External' },
    { label: 'AC', value: 'AC' },
    { label: 'Functional', value: 'Functional' },
  ];

  selectedRoleType: any = null;

  client_user_types = [
    { label: 'User', value: 1 },
    { label: 'Editor', value: 2 },
    { label: 'Admin', value: 3 },
    { label: 'Developer', value: 4 },
    { label: 'Super Admin', value: 5 },
  ];

  constructor(
    private appService: AppService,
    private userAdminService: UserAdminService,
    private alertService: GtwSnackbarService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private adminScreenContext: AdminScreenContextService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.bootstrapFromRow();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.bootstrapFromRow();
      })
    );
    this.bootstrapFromRow();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const raw = resolveRowPayload(this.adminScreenContext, this.gtwWorkspace);
    this.rowData = Array.isArray(raw)
      ? (raw[0] && typeof raw[0] === 'object' ? raw[0] : {})
      : (raw || {});
  }

  private bootstrapFromRow(): void {
    const nextId = this.rowData?.SSO_ID || this.rowData?.SSO || this.rowData?.user_id || this.rowData?.sso_id || '';
    if (!nextId) {
      return;
    }
    if (nextId === this.user_id) {
      return;
    }
    this.user_id = nextId;
    this.getData();
  }

  getData(): void {
    this.loading = true;
    this.userAdminService.getUserDetails({ user_id: this.user_id }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data?.callSuccess === '1') {
          this.user = data.user;
          this.setUser();
        } else {
          this.alertService.openSnackBar('User could not be found.', 'gtw-snackbar--red');
        }
        this.loading = false;
      },
      () => {
        this.loading = false;
      }
    );

    this.loadClientsUser();
  }

  private loadClientsUser(): void {
    this.userAdminService.getUserClients({ user_id: this.user_id }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        const data = response?.data || response;
        if (data?.clientResult) {
          this.clientList = data.clientResult;
          this.defaultClientData = [];
          _.each(this.clientList, (item: any) => {
            if (item.client_key === 3) {
              this.defaultClientData.push(item);
            }
          });
        }
      },
      () => {
        // silently fail - clients section will just be empty
      }
    );
  }

  private setUser(): void {
    this.active = parseInt(this.user.active_status, 10) === 1;

    const typeIdx = _.findIndex(this.user_types, { value: this.user.user_type_key });
    this.selectedUserType = typeIdx >= 0 ? this.user_types[typeIdx] : this.user_types[0];

    const roleIdx = _.findIndex(this.role_types, { value: this.user.role });
    this.selectedRoleType = roleIdx >= 0 ? this.role_types[roleIdx] : null;

    this.buildAvatar();
    this.emitEvent.emit(this.user.display_name + ' - ( ' + this.user.sso_id + ' )');

    if (this.selectedUserType.value <= 3) {
      this.clientList = this.clientList.filter((item: any) => item.client_key !== 3);
    }
  }

  private buildAvatar(): void {
    const name = this.user.display_name || '';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      this.avatarInitials = (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    } else if (parts.length === 1 && parts[0]) {
      this.avatarInitials = parts[0].substring(0, 2).toUpperCase();
    } else {
      this.avatarInitials = '?';
    }
  }

  compareByValue(a: any, b: any): boolean {
    return a && b && a.value === b.value;
  }

  onUserTypeChange(): void {
    this.isDefaultClientAllowed();
  }

  onRoleTypeChange(): void {
    // role changed, no side effects
  }

  onActiveChange(event: any): void {
    this.active = event?.checkboxState ?? event;
  }

  onClientActiveChange(event: any, client: any): void {
    client.active = (event?.checkboxState ?? event) ? 1 : 0;
    client.updated = true;
  }

  changeClient(client: any): void {
    client.updated = true;
  }

  isDefaultClientAllowed(): void {
    if (this.selectedUserType?.value > 3) {
      const found = this.clientList.some((item: any) => item.client_key === 3);
      if (!found && this.defaultClientData.length > 0) {
        this.clientList = [...this.clientList, this.defaultClientData[0]];
      }
    } else {
      this.clientList = this.clientList.filter((item: any) => item.client_key !== 3);
    }
  }

  deleteImage(): void {
    this.alertService.openSnackBar('Delete image functionality not yet implemented.', 'gtw-snackbar--yellow');
  }

  save(): void {
    if (this.crudLoading) {
      this.alertService.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }

    const data: any = {
      sso_id: this.user.sso_id,
      active: this.active ? 1 : 0,
      client_active: this.client_active,
      user_type: this.selectedUserType?.value,
      role: this.selectedRoleType?.value,
    };

    const updatedClients: any[] = [];
    _.each(this.clientList, (value: any) => {
      if (value.updated) {
        updatedClients.push({
          client_key: value.client_key,
          active: value.active,
          user_type_key: value.user_type_key,
        });
      }
    });

    this.crudLoading = true;
    data.clients = JSON.stringify(updatedClients);

    this.userAdminService.updateUser(data).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        if (result?.callSuccess === '1') {
          const currentUserSso = this.appService.USER_SETTINGS?.user?.sso_id;
          if (this.user_id !== currentUserSso) {
            this.getData();
          }
          this.crudLoading = false;
          this.alertService.openSnackBar('User has been updated', 'gtw-snackbar--green');
        } else {
          this.getData();
          this.crudLoading = false;
        }
      },
      () => {
        this.crudLoading = false;
        this.alertService.openSnackBar('An error occurred while saving.', 'gtw-snackbar--red');
      }
    );
  }

  cancel(): void {
    this.emitEvent.emit('cancel');
  }
}
